/**
 * StageSection — one numbered stage on the decision page.
 *
 * The page is the lifecycle, top to bottom. The live stage is open; earlier
 * stages collapse to a one-line summary with an Edit affordance; later
 * stages are visible but quiet, so the person always sees where they are
 * and what comes next.
 */

import type { ReactNode } from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export type StageState = 'done' | 'live' | 'upcoming';

export function StageSection({
  number,
  title,
  summary,
  state,
  expanded,
  onToggle,
  children,
}: {
  number: number;
  title: string;
  /** One line shown when collapsed, e.g. "Decided by Wes · due Friday · 4 options". */
  summary?: string;
  state: StageState;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const badge =
    state === 'done'
      ? 'bg-[var(--green)]/15 text-[var(--green)] dark:text-[var(--text-ter)]'
      : state === 'live'
        ? 'bg-[var(--slate)] text-white'
        : 'bg-[var(--surface)] text-[var(--text-dim)]';

  return (
    <section
      aria-labelledby={`stage-${number}-title`}
      className={`border ${state === 'live' ? 'border-[var(--rule-bright)]' : 'border-[var(--rule)]'} bg-[var(--bg-raised)]`}
      style={state === 'live' ? { borderColor: 'var(--rule-bright)' } : undefined}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-[var(--hover-tint)] transition-colors"
      >
        <span className={`flex-shrink-0 w-8 h-8 flex items-center justify-center font-mono text-xs font-semibold ${badge}`} aria-hidden="true">
          {state === 'done' ? <CheckIcon className="w-4 h-4" /> : number}
        </span>
        <span className="flex-1 min-w-0">
          <span id={`stage-${number}-title`} className="block font-heading font-semibold text-base text-[var(--text-pri)]">
            {title}
          </span>
          {!expanded && summary && (
            <span className="block text-sm text-[var(--text-sec)] truncate mt-0.5">{summary}</span>
          )}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-[var(--text-dim)] flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && <div className="px-5 pb-6 pt-1 border-t border-[var(--rule)]">{children}</div>}
    </section>
  );
}
